// Klinik şablonu oluşturma/düzenleme drawer'ı (design-principles §7, §8):
// DrawerHeader + kutusuz alanlar (DrawerField, variant="standard") + DrawerFooter.
// Düzenlemede gövdenin sonunda "Durum" bölümü: aktif/pasif geçişi (eski satır
// içi "Pasif Yap" butonunun yeri).

import React from "react";
import { Box, Button, Drawer, Typography } from "@mui/material";
import { DrawerHeader, DrawerFooter } from "../atoms";
import ActionLoadingOverlay from "../common/ActionLoadingOverlay";
import { COLORS, TYPO } from "../../styles/uiConstants";
import { StatusPill } from "./ClinicRecordList/recordTableParts";

/** Drawer alan ızgarası (§7): rowGap 2.5, columnGap 2. */
export const drawerGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  rowGap: 2.5,
  columnGap: 2,
};

/**
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {React.ReactNode} icon
 * @param {string} title
 * @param {string} [subtitle]
 * @param {() => void} onSubmit
 * @param {string} [submitLabel]
 * @param {boolean} [submitDisabled]
 * @param {boolean} [loading]
 * @param {{isActive: boolean, onToggle: () => void, loading?: boolean}} [statusControl] - yalnız düzenlemede
 * @param {React.ReactNode} [headerExtra] - gövdenin üstünde (ör. test için otomatik doldur)
 * @param {number} [width=520]
 */
export default function TemplateEditorDrawer({
  open,
  onClose,
  icon,
  title,
  subtitle,
  onSubmit,
  submitLabel = "Kaydet",
  submitDisabled,
  loading,
  statusControl,
  headerExtra,
  width = 520,
  children,
}) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={loading ? undefined : onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: width }, display: "flex", flexDirection: "column", bgcolor: COLORS.white },
      }}
    >
      <DrawerHeader icon={icon} title={title} subtitle={subtitle} onClose={onClose} />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, py: 2.5, position: "relative" }}>
        <ActionLoadingOverlay show={Boolean(loading)} variant="save" text="Kaydediliyor..." />
        {headerExtra ? <Box sx={{ mb: 2 }}>{headerExtra}</Box> : null}
        {children}

        {statusControl ? (
          <Box
            sx={{
              mt: 3,
              pt: 2.5,
              borderTop: `1px solid ${COLORS.slate100}`,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: TYPO.label, fontWeight: 600, color: COLORS.slate500 }}>Durum</Typography>
                <StatusPill
                  label={statusControl.isActive ? "Aktif" : "Pasif"}
                  tone={statusControl.isActive ? "success" : "neutral"}
                />
              </Box>
              <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate400, mt: 0.5 }}>
                {statusControl.isActive
                  ? "Pasife alınan şablon muayene ve tedavi ekranlarında önerilmez."
                  : "Aktifleştirince şablon yeniden önerilir."}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={statusControl.onToggle}
              disabled={statusControl.loading || loading}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: TYPO.body,
                borderRadius: 2,
                px: 2,
                color: COLORS.slate700,
                borderColor: COLORS.slate300,
                "&:hover": { borderColor: COLORS.slate400, bgcolor: COLORS.slate50 },
              }}
            >
              {statusControl.isActive ? "Pasife al" : "Aktifleştir"}
            </Button>
          </Box>
        ) : null}
      </Box>

      <DrawerFooter
        onCancel={onClose}
        cancelLabel="Vazgeç"
        onSubmit={onSubmit}
        submitLabel={submitLabel}
        loadingLabel="Kaydediliyor..."
        loading={loading}
        disabled={submitDisabled}
        fullWidth
      />
    </Drawer>
  );
}
